/**
 * frontend/src/context/WorkspaceContext.js
 * 
 * React Context provider for workspace and change request state management.
 * It centralizes data fetching and updates for workspaces, members, and change requests.
 * 
 * Common functions:
 * - fetchWorkspaces: Retrieves all workspaces for the current user.
 * - fetchWorkspace: Fetches detailed information for a specific workspace.
 * - createWorkspace/joinWorkspace: Handles workspace lifecycle operations.
 * - fetchChangeRequests: Loads change requests for a workspace.
 * - createChangeRequest/updateChangeRequest: Manages change request lifecycle.
 * - fetchActivity: Retrieves the activity log for a workspace.
 * - useWorkspace: Custom hook to access workspace context.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { workspacesAPI, changeRequestsAPI, templatesAPI } from '../services/api';

const WorkspaceContext = createContext(null);

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activity, setActivity] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchWorkspaces = useCallback(async () => {
    setLoading(true);
    try {
      const response = await workspacesAPI.getAll();
      setWorkspaces(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWorkspace = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await workspacesAPI.get(id);
      setCurrentWorkspace(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch workspace:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const createWorkspace = async (name) => {
    const response = await workspacesAPI.create({ name });
    setWorkspaces((prev) => [...prev, response.data]);
    return response.data;
  };

  const updateWorkspace = async (id, data) => {
    const response = await workspacesAPI.update(id, data);
    setCurrentWorkspace(response.data);
    setWorkspaces((prev) =>
      prev.map((w) => (w.id === id ? response.data : w))
    );
    return response.data;
  };

  const joinWorkspace = async (inviteCode) => {
    const response = await workspacesAPI.join(inviteCode);
    setWorkspaces((prev) => [...prev, response.data]);
    return response.data;
  };

  const regenerateInviteCode = async (id) => {
    const response = await workspacesAPI.regenerateInvite(id);
    setCurrentWorkspace(response.data);
    return response.data;
  };

  const fetchMembers = useCallback(async (workspaceId) => {
    try {
      const response = await workspacesAPI.getMembers(workspaceId);
      setMembers(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch members:', error);
      throw error;
    }
  }, []);

  const updateMemberRole = async (workspaceId, userId, role) => {
    const response = await workspacesAPI.updateMember(workspaceId, userId, { role });
    setMembers((prev) =>
      prev.map((m) => (m.user_id === userId ? response.data : m))
    );
    return response.data;
  };

  const removeMember = async (workspaceId, userId) => {
    await workspacesAPI.removeMember(workspaceId, userId);
    setMembers((prev) => prev.filter((m) => m.user_id !== userId));
  };

  const inviteMembers = async (workspaceId, emails) => {
    const response = await workspacesAPI.inviteMembers(workspaceId, emails);
    return response.data;
  };

  const fetchInvites = useCallback(async (workspaceId) => {
    try {
      const response = await workspacesAPI.getInvites(workspaceId);
      setInvites(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch invites:', error);
      throw error;
    }
  }, []);

  const cancelInvite = async (workspaceId, inviteId) => {
    await workspacesAPI.cancelInvite(workspaceId, inviteId);
    setInvites((prev) => prev.filter((i) => i.id !== inviteId));
  };

  const fetchChangeRequests = useCallback(async (workspaceId) => {
    try {
      const response = await changeRequestsAPI.getAll(workspaceId);
      setChangeRequests(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch change requests:', error);
      throw error;
    }
  }, []);

  const createChangeRequest = async (workspaceId, data) => {
    const response = await changeRequestsAPI.create(workspaceId, data);
    setChangeRequests((prev) => [response.data, ...prev]);
    return response.data;
  };

  const updateChangeRequest = async (id, data) => {
    const response = await changeRequestsAPI.update(id, data);
    setChangeRequests((prev) =>
      prev.map((cr) => (cr.id === id ? response.data : cr))
    );
    return response.data;
  };

  const fetchActivity = useCallback(async (workspaceId, limit = 50) => {
    try {
      const response = await workspacesAPI.getActivity(workspaceId, limit);
      setActivity(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch activity:', error);
      throw error;
    }
  }, []);

  const fetchTemplates = useCallback(async (workspaceId) => {
    try {
      const response = await templatesAPI.getAll(workspaceId);
      setTemplates(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      throw error;
    }
  }, []);

  const createTemplate = async (workspaceId, data) => {
    const response = await templatesAPI.create(workspaceId, data);
    setTemplates((prev) => [...prev, response.data]);
    return response.data;
  };

  const updateTemplate = async (id, data) => {
    const response = await templatesAPI.update(id, data);
    setTemplates((prev) =>
      prev.map((t) => (t.id === id ? response.data : t))
    );
    return response.data;
  };

  const deleteTemplate = async (id) => {
    await templatesAPI.delete(id);
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  };

  const fetchAuditLogs = useCallback(async (workspaceId, filters = {}) => {
    try {
      const response = await workspacesAPI.getAuditLogs(workspaceId, filters);
      setAuditLogs(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      throw error;
    }
  }, []);

  const value = {
    workspaces,
    currentWorkspace,
    members,
    invites,
    changeRequests,
    templates,
    activity,
    auditLogs,
    loading,
    fetchWorkspaces,
    fetchWorkspace,
    createWorkspace,
    updateWorkspace,
    joinWorkspace,
    regenerateInviteCode,
    fetchMembers,
    updateMemberRole,
    removeMember,
    inviteMembers,
    fetchInvites,
    cancelInvite,
    fetchChangeRequests,
    createChangeRequest,
    updateChangeRequest,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    fetchActivity,
    fetchAuditLogs,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};
